import ErrorIcon from "@mui/icons-material/Error";
import { Badge, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  VisitEventStatusEnum,
  VisitResourceErrandStatusEnum,
  VisitResourceVirtualProviderStatusEnum as VStatusEnum,
} from "~/graphql/__generated__/graphql";
import { calculateInfoColor, visitResourceStatus } from "~/utils/statuses";
import { CustomMbscCalendarEventData } from "~/utils/eventsHelper";
import { CommandCenterLocationState } from "~/components/command/DateSelector";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import { visitResourceNeedsAttention } from "~/utils/visitsHelper";
import PrecautionMarker from "~/components/command/PrecautionMarker";
import VisitStatusTooltip, { StatusLogEntry } from "./VisitStatusTooltip";

type PatientVisitEventProps = {
  event: CustomMbscCalendarEventData;
  data: VisitRenderType;
  extraContent?: JSX.Element;
  badState?: boolean;
};

export type VisitRenderType = {
  isRequest: boolean,
  badState: boolean,
  visit: { id: number, eventStatus: VisitEventStatusEnum, eventLog?: StatusLogEntry[] },
  patient: { firstName: string, lastName: string } | null,
  visitType: { name: string },
  errandType?: { name: string },
  errandStatus?: VisitResourceErrandStatusEnum | null,
  providerId?: number | null,
  virtualProviderStatus?: VStatusEnum | null,
  providerName?: string,
  viewMode?: "provider" | "patient",
  locked?: boolean,
  precautionIds?: number[],
}

const PatientVisitEvent: React.FC<PatientVisitEventProps> = ({ event, data, extraContent }) => {
  const { visit, badState, isRequest } = data;
  const backgroundColor = "#00BFB3";
  const isPending = event.cssClass?.includes("dashed-border");
  const navigate = useNavigate();
  const locationState = useLocation().state as CommandCenterLocationState;
  const isLocked = data.locked ?? false;
  const [errorHovered, setErrorHovered] = useState(false);
  const lockedColors = {
    background: "#E0E0E0",
    border: "#BDBDBD",
  };
  const eventStatus = visitResourceStatus({
    errandStatus: data.errandStatus,
    visit,
  });

  // Other parts of the app send the user to the command center with a visitId in location.state; if we have one, highlight
  // that visit on the timeline.
  const highlight = event.referenceId === locationState?.visitResourceId;

  const infoColor = calculateInfoColor({ eventStatus });
  let background = isLocked ? lockedColors.background : (infoColor.backgroundColor || backgroundColor);
  let border = isLocked ? `1px solid ${lockedColors.border}` : `1px solid ${infoColor.text}`;

  if (badState) {
    border = "4px solid red";
  }

  // @TODO: get back to this color handling.
  if (isRequest && !isLocked) {
    border = badState ? border : "2px dashed #2E7D32";
    background = "white";
  }

  // Errands show no border unless they are moved (dashed pending) or in a warning
  // state. A transparent 1px matches the sizing of every other event.
  if (data.errandType && !badState && !isPending) {
    border = "1px solid transparent";
  }

  const providerId = useCurrentOrg()?.providerId;
  // TS doesn't notice the type-narrowing; 100% sure that providerId is defined on data here.
  const needsAttention = data.providerId && visitResourceNeedsAttention(data as any, providerId);

  const element = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (highlight) {
      // Mobiscroll has "infinite scroll" style logic - it only renders elements that are currently in the viewport,
      // and as the user scrolls - especially during drag and drop - this can cause components to repeatedly be discarded
      // and re-created. Thus, a simple useEffect designed to fire once on page load is insufficient; we need to
      // remove the highlighted element from state after initial animation so that we don't keep re-firing the scrollTo
      // and (less importantly) the highlight animation.
      element.current?.addEventListener("animationend", () => {
        navigate(location.pathname, { state: { ...locationState, visitResourceId: undefined }});
      }, false);
    }
  }, [highlight]);

  // The hover effects are done directly in the DOM, rather than through react, because, frankly,
  // it's simpler than keeping track of state across sibling components.
  const hover = useRef(false);
  const hoverTimeout = useRef<undefined | ReturnType<typeof setTimeout>>(undefined);
  const selector = `.ghost-event.ghost-visit-${visit.id}, .dashed-connection.visit-${visit.id}`
  const clearHover = useCallback(() => {
    document.querySelectorAll(selector).forEach(e => e.classList.remove("hover"));
  }, [visit.id]);

  const onHover = useCallback(() => {
    hover.current = true;
    clearTimeout(hoverTimeout.current)
    hoverTimeout.current = undefined;
    document.querySelectorAll(selector).forEach(e => e.classList.add("hover"));
  }, [visit.id, hover, hoverTimeout]);

  const onUnhover = useCallback(() => {
    hover.current = false;
    clearTimeout(hoverTimeout.current)
    hoverTimeout.current = setTimeout(clearHover, 1500);
  }, [clearHover, hover, hoverTimeout]);

  const eventBlock = (
    <div
      className="md-timeline-template-event"
      style={{ 
        border,
        fontSize: "13px",
        background,
        animation: highlight ? "pulse-animation 1s 3" : needsAttention ? "pulse-animation 1s infinite" : undefined,
      }}
      ref={element}
      onMouseEnter={onHover}
      onTouchStart={onHover}
      onMouseLeave={onUnhover}
      onTouchEnd={onUnhover}
    >
      <div className="md-timeline-template-event-cont">
        {(isRequest || isPending) ? <Badge sx={{ position: 'absolute', left: 7, top: 20 }} color="success" variant="dot" /> : null}
        <span className="md-timeline-template-title">
          {extraContent} {event.tooltip}
        </span>
        {badState && (
          <Tooltip
            title={event.tooltip}
            placement="top"
            arrow
            onOpen={() => setErrorHovered(true)}
            onClose={() => setErrorHovered(false)}
          >
            <ErrorIcon sx={{
              position: "absolute",
              top: "12px",
              right: "5px",
              fontSize: "20px",
              color: "black",
            }} />
          </Tooltip>
        )}
      </div>
    </div>
  );

  if (isRequest || !data.providerId) {
    return eventBlock;
  }

  const patientName = data.patient
    ? `${data.patient.firstName || ""} ${data.patient.lastName || ""}`.trim()
    : "";
  const eventName = data.errandType
    ? `Errand: ${data.errandType.name}`
    : data.visitType?.name;

  return (
    <VisitStatusTooltip
      title={[eventName, patientName].filter((part) => !!part).join(" - ")}
      status={eventStatus}
      logEntry={data.errandType ? undefined : visit.eventLog?.[0]}
      suppressed={errorHovered}
    >
      {eventBlock}
    </VisitStatusTooltip>
  );
};

export const renderScheduleEvent = (event: CustomMbscCalendarEventData, data: VisitRenderType) => {
  const { visit, patient, visitType, errandType, badState } = data;

  const infoColor = calculateInfoColor({
    eventStatus: visitResourceStatus({ errandStatus: data.errandStatus, visit }),
  });
  const isLocked = data.locked ?? false;
  const textColor = isLocked ? "#616161" : infoColor.text;
  const title = errandType?.name ?? visitType?.name;

  const extraContent = (
    <Stack
      direction="column" // Set direction to column
      justifyContent="space-between" // Space out elements
      alignItems="left" // Align items vertically
      style={{
        textOverflow: "ellipsis",
        overflow: "hidden",
        flexWrap: "nowrap", // Prevent wrapping
      }}
    >
      {title && (
        <Typography
          display="inline-block"
          style={{
            color: textColor,
            fontWeight: 700,
            fontSize: "14px",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
        >
          {title}
        </Typography>
      )}
      {data.viewMode === "provider" && patient && (
        <Typography
          display="inline-block"
          sx={{
            fontSize: "14px",
            color: textColor,
            fontWeight: "normal",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
        >
          {`${patient.firstName || ""} ${patient.lastName || ""}`}
        </Typography>
      )}
      {data.viewMode === "patient" && data.providerName && (
        <Typography
          display="inline-block"
          sx={{
            fontSize: "12px",
            color: textColor,
            fontWeight: "normal",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
        >
          {data.providerName}
        </Typography>
      )}
      {!!data.precautionIds?.length && (
        <PrecautionMarker
          precautionIds={data.precautionIds}
          size={16}
          sx={{
            position: "absolute",
            top: "14px",
            right: badState ? "27px" : "5px",
          }}
        />
      )}
    </Stack>
  );
  return (
    <PatientVisitEvent
      event={event}
      data={data}
      extraContent={extraContent}
    />
  );
};
